import { Link } from "react-router-dom";
import { Compass, Stethoscope, Briefcase, FlaskConical, GraduationCap, Sparkles, Mail, ArrowRight, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { SUPPORT_EMAIL } from "@/config/contact";

const categories = [
  { icon: Stethoscope, title: "Clinical Rotations", blurb: "Pharmacy, nursing, medical, dental, veterinary and allied health placements." },
  { icon: Briefcase, title: "Business Internships", blurb: "Finance, marketing, consulting, HR and operations roles." },
  { icon: FlaskConical, title: "Research Programs", blurb: "Lab, clinical trial and academic research opportunities." },
  { icon: GraduationCap, title: "Education & Public Service", blurb: "Student teaching, government fellowships and nonprofit internships." },
];

export default function OpportunitiesPage() {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-accent/5" />
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-accent/10 blur-3xl" />

        <div className="relative max-w-5xl mx-auto px-6 py-20 md:py-28">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold uppercase tracking-wider mb-6">
            <Sparkles className="w-3.5 h-3.5" />
            Coming Soon
          </div>
          <h1 className="font-serif text-4xl md:text-6xl font-bold text-foreground leading-tight">
            Find your next{" "}
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              rotation or internship
            </span>
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-2xl leading-relaxed">
            A curated board of clinical rotations, internships and fellowships — hand-picked from
            programs students actually rate highly on Vecurate.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/rotations">
              <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-6 font-semibold shadow-md">
                Browse rotation reviews
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <Link to="/internships">
              <Button size="lg" variant="outline" className="h-12 px-6 font-semibold bg-white">
                Browse internship reviews
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Coming soon notice */}
      <section className="max-w-5xl mx-auto px-6 -mt-8 relative z-10">
        <Card className="p-6 md:p-8 border-2 border-accent/30 bg-gradient-to-r from-accent/5 to-primary/5 shadow-lg">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-accent/15 text-accent flex items-center justify-center flex-shrink-0">
              <Clock className="w-6 h-6" />
            </div>
            <div>
              <h2 className="font-serif text-xl md:text-2xl font-bold text-foreground">
                Opportunities will soon be added
              </h2>
              <p className="mt-2 text-muted-foreground leading-relaxed">
                We&apos;re still building this board. Real, verified rotation and internship
                listings will start appearing here shortly. In the meantime, explore what
                other students have said about programs they&apos;ve completed.
              </p>
            </div>
          </div>
        </Card>
      </section>

      {/* Categories */}
      <section className="max-w-5xl mx-auto px-6 py-16 md:py-20 w-full">
        <div className="flex items-center gap-2 mb-2">
          <Compass className="w-5 h-5 text-primary" />
          <p className="text-sm font-semibold uppercase tracking-wider text-primary">What you&apos;ll find here</p>
        </div>
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground">Categories we&apos;re curating</h2>

        <div className="mt-10 grid gap-5 sm:grid-cols-2">
          {categories.map(({ icon: Icon, title, blurb }) => (
            <Card
              key={title}
              className="p-6 border border-border bg-white hover:border-primary/40 hover:shadow-md transition-all group"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
                <Icon className="w-6 h-6" />
              </div>
              <h3 className="font-serif text-xl font-bold text-foreground">{title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{blurb}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 pb-20 w-full">
        <Card className="p-8 md:p-10 bg-primary text-primary-foreground border-none shadow-xl">
          <div className="flex flex-col md:flex-row md:items-center gap-6 justify-between">
            <div>
              <h3 className="font-serif text-2xl md:text-3xl font-bold">
                Know an opportunity worth featuring?
              </h3>
              <p className="mt-2 text-primary-foreground/80 max-w-xl">
                Send us a tip and we&apos;ll consider adding it once the board launches.
              </p>
            </div>
            <a href={`mailto:${SUPPORT_EMAIL}?subject=Opportunity%20suggestion`}>
              <Button size="lg" variant="secondary" className="h-12 px-6 font-semibold gap-2">
                <Mail className="w-4 h-4" />
                Suggest one
              </Button>
            </a>
          </div>
        </Card>
      </section>

      <Footer />
    </div>
  );
}
